import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Icon } from '@iconify/react';

const variantConfig = {
  error: {
    backgroundColor: '#FDEDED',
    borderColor: '#F5C2C0',
    textColor: '#8C1D18',
    iconColor: '#DA342E',
    icon: 'solar:close-circle-bold',
  },
  warning: {
    backgroundColor: '#FFF5E1',
    borderColor: '#F0DBB0',
    textColor: '#7A4F00',
    iconColor: '#8A5A00',
    icon: 'mdi:alert-outline',
  },
  success: {
    backgroundColor: '#EEF7E6',
    borderColor: '#CDE5B8',
    textColor: '#2F5203',
    iconColor: '#4F8506',
    icon: 'solar:check-circle-bold',
  },
};

/**
 * A message that stays on screen until it is dismissed, for things the user has to read and act on.
 *
 * A toast is wrong for a refusal: it slides away after a few seconds, and by design nothing about it
 * survives long enough to be re-read. "The user type X cannot be deleted: 12 users are assigned to
 * it" is an instruction, not a notification — the reader needs it in front of them while they go and
 * deal with those 12 users. Toasts remain the right shape for confirmations, which are read once and
 * need no action.
 */
export default function InlineAlert({ message, onClose, variant = 'error', sx = {} }) {
  if (!message) return null;

  const config = variantConfig[variant] || variantConfig.error;

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        mb: 2,
        borderRadius: '12px',
        backgroundColor: config.backgroundColor,
        border: `1px solid ${config.borderColor}`,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', height: 24, flexShrink: 0 }}>
        <Icon icon={config.icon} width="22" height="22" color={config.iconColor} />
      </Box>

      <Typography
        sx={{
          flexGrow: 1,
          minWidth: 0,
          fontSize: 15,
          lineHeight: '24px',
          letterSpacing: '0.15px',
          color: config.textColor,
          // Backend refusals name the obstacle and can run long; wrap rather than truncate, since
          // the count and the instruction are usually at the end of the sentence.
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {message}
      </Typography>

      {onClose && (
        <IconButton
          onClick={onClose}
          aria-label="Dismiss message"
          size="small"
          sx={{ flexShrink: 0, color: config.textColor, mt: '-2px' }}
        >
          <Icon icon="material-symbols:close-rounded" width="18" height="18" />
        </IconButton>
      )}
    </Box>
  );
}
